import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { organizations } from '../drizzle/schema';

export const ANALYTICS_EXPORT_TARGET = 'analytics';

export const ExpenseStatusEnum = z.enum([
  'open',
  'approving',
  'approved',
  'overdue',
  'rejected',
  'done',
]);
export type ExpenseStatus = z.infer<typeof ExpenseStatusEnum>;
export const ReportStatusEnum = ExpenseStatusEnum;
export type ReportStatus = ExpenseStatus;

export const OrgRoleEnum = z.enum(['basic_member', 'admin', 'org:manager', 'org:accountant']);
export type OrgRole = z.infer<typeof OrgRoleEnum>;

export const OrgRoleNameEnum = z.enum(['Member', 'Manager', 'Accountant', 'Admin']);
export type OrgRoleName = z.infer<typeof OrgRoleNameEnum>;
export type RuleType =
  | 'comment'
  | 'amount'
  | 'receipt'
  | 'distance'
  | 'duplicate'
  | 'category'
  | 'issuer'
  | 'vat'
  | 'report'
  | 'client'
  | 'cost_centre'
  | 'country';
export type CategoryData = {
  amount_daily: number;
  amount_monthly: number;
  comment: number;
  receipt: number;
  distance: number;
};
export const RuleEnum = z.enum([
  'comment',
  'amount_daily',
  'amount_monthly',
  'receipt',
  'distance',
  'issuer',
  'category',
  'vat',
  'client',
  'cost_centre',
  'country',
  'report',
]);
export type Rules = z.infer<typeof RuleEnum>;

// 'Salmon' | 'Tuna' | 'Trout'
import {
  categories,
  clients,
  costcentres,
  expenses,
  expensesHistory,
  mileages,
  mileagesHistory,
  reports,
  rules,
  users,
} from '@/drizzle/schema';

export const selectCategorySchema = createSelectSchema(categories);
export const selectCategoriesSchema = createSelectSchema(categories).array();
export const selectClientSchema = createSelectSchema(clients);
export const selectClientsSchema = createSelectSchema(clients).array();
export const insertClientSchema = createInsertSchema(clients);
export const insertOrganizationSchema = createInsertSchema(organizations);
export const companySetupStatusSchema = z.enum(['pending', 'done']);
export const companySetupStepValueSchema = z.enum(['completed', 'skipped']);
export const companySetupStepsSchema = z.object({
  categories: companySetupStepValueSchema.optional(),
  costcentres: companySetupStepValueSchema.optional(),
  mileage: companySetupStepValueSchema.optional(),
  'rules-receipt': companySetupStepValueSchema.optional(),
  'rules-meals': companySetupStepValueSchema.optional(),
  invite: companySetupStepValueSchema.optional(),
});
export type CompanySetupStatus = z.infer<typeof companySetupStatusSchema>;
export type CompanySetupStepValue = z.infer<typeof companySetupStepValueSchema>;
export type CompanySetupSteps = z.infer<typeof companySetupStepsSchema>;

const baseSelectOrganizationSchema = createSelectSchema(organizations);
export const selectOrganizationSchema = baseSelectOrganizationSchema.extend({
  recipients: z.array(z.string().email()).nullable(),
  form_field_settings: z.record(z.string(), z.string()).default({}),
  accountant_role_enabled: z.boolean().default(false),
  default_paid_with_personal_funds: z.boolean().default(false),
  company_setup_status: companySetupStatusSchema.optional().default('done'),
  company_setup_steps: companySetupStepsSchema.nullish(),
  hide_company_setup_banner: z.boolean().optional().default(false),
});
export const selectCostCentreSchema = createSelectSchema(costcentres);
export const selectCostCentresSchema = createSelectSchema(costcentres).array();
export const insertCostCentreSchema = createInsertSchema(costcentres);
const baseSelectUserSchema = createSelectSchema(users, {
  password: z.string().optional(),
  api_token: z.string().optional().nullable(),
  google2fa_secret: z.string().optional().nullable(),
  remember_token: z.string().optional().nullable(),
  gdrive_access_token: z.string().optional().nullable(),
  gdrive_refresh_token: z.string().optional().nullable(),
  gdrive_token_expires_at: z.string().optional().nullable(),
});
export const selectUserSchema = baseSelectUserSchema.extend({
  iban: z.string().nullable().optional(),
});
export const selectUsersSchema = baseSelectUserSchema
  .extend({
    iban: z.string().nullable().optional(),
    left_at: z.string().optional().nullable(),
  })
  .array();
export const selectRuleSchema = createSelectSchema(rules);
export const selectRulesSchema = createSelectSchema(rules).array();
export const insertRulesSchema = createInsertSchema(rules);

// New types for expense splitting
export const ExpenseSplitSchema = z.object({
  id: z.number(),
  uuid: z.string().nullable(),
  expense_id: z.number().nullable(),
  currency: z.string().nullable(),
  amount_default_currency: z.string().nullable(),
  vat: z.string().optional().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  amount: z.string().nullable(),
  category_id: z.number().optional().nullable(),
  category_title: z.string().optional().nullable(),
});

export type ExpenseSplit = z.infer<typeof ExpenseSplitSchema>;

export type ExpenseInsertWithSplits = ExpenseInsert & {
  splits?: ExpenseSplit[];
};

export type Split = {
  amount: string;
  currency: string;
  category_id?: number | null | undefined;
  vat?: string | null;
  amount_default_currency: string;
};

export const insertExpensesSchema = createInsertSchema(expenses);

export const selectMileageSchema = createSelectSchema(mileages).extend({
  reports_id: z.number().optional().nullable(),
  client_id: z.number().optional().nullable(),
  cost_centre_id: z.number().optional().nullable(),
  comment: z.string().optional().nullable(),
  client_name: z.string().optional().nullable(),
  report_title: z.string().optional().nullable(),
  cost_centre_name: z.string().optional().nullable(),
});

export const selectExpenseSchema = createSelectSchema(expenses).extend({
  reports_id: z.number().optional().nullable(),
  issuer: z.string().optional().nullable(),
  category_id: z.number().optional().nullable(),
  country: z.string().optional().nullable(),
  vat: z.string().optional().nullable(),
  client_id: z.number().optional().nullable(),
  cost_centre_id: z.number().optional().nullable(),
  comment: z.string().optional().nullable(),
  client_name: z.string().optional().nullable(),
  report_title: z.string().optional().nullable(),
  cost_centre_name: z.string().optional().nullable(),
  hasReceipt: z
    .number()
    .nullish()
    .transform((val) => val ?? 0),
});

export const selectExpensesSchema = selectExpenseSchema.extend({
  category_title: z.string().optional().nullable(),
  is_duplicate: z.boolean().optional().nullable(),
  splits: z.array(ExpenseSplitSchema).optional().nullable(),
});
const historyEditorSchema = z
  .object({
    id: z.number().optional().nullable(),
    name: z.string().optional().nullable(),
    last_name: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
  })
  .optional()
  .nullable();

export const selectMileageHistorySchema = createSelectSchema(mileagesHistory).extend({
  editor: historyEditorSchema,
});
export const selectExpenseHistorySchema = createSelectSchema(expensesHistory).extend({
  editor: historyEditorSchema,
});
export const selectMileageWithHistorySchema = selectMileageSchema.extend({
  history: selectMileageHistorySchema.array(),
});
export const selectExpenseWithHistorySchema = selectExpenseSchema.extend({
  history: selectExpenseHistorySchema.array(),
  category_title: z.string().optional().nullable(),
  is_duplicate: z.boolean().optional().nullable(),
  splits: z.array(ExpenseSplitSchema).optional().nullable(),
});

export const insertMileageSchema = createInsertSchema(mileages);
export const selectReportsSchema = createSelectSchema(reports).array();
export const insertReportsSchema = createInsertSchema(reports);
export const selectExpensesArraySchema = createSelectSchema(expenses).array();
export type MileageHistory = z.infer<typeof selectMileageHistorySchema>;
export type ExpenseHistory = z.infer<typeof selectExpenseHistorySchema>;
export type User = z.infer<typeof selectUserSchema>;
export type Expense = z.infer<typeof selectExpensesSchema>;
export type ExpenseInsert = Omit<
  z.infer<typeof insertExpensesSchema>,
  'organization_id' | 'amount_default_currency'
> &
  Partial<Pick<z.infer<typeof insertExpensesSchema>, 'amount_default_currency'>> & {
    client_name?: string | null;
    report_title?: string | null;
    cost_centre_name?: string | null;
  };
export type Mileage = z.infer<typeof selectMileageSchema>;
export type Category = z.infer<typeof selectCategorySchema>;
export type Client = z.infer<typeof selectClientSchema>;
export type CostCentre = z.infer<typeof selectCostCentreSchema>;
export type Rule = z.infer<typeof selectRuleSchema>;
export type RuleInsert = z.infer<typeof insertRulesSchema>;

export interface BankDetails {
  iban: string | null;
  account_holder: string;
}
export type MileageUpdate = Omit<Mileage, 'created_at' | 'updated_at'>;
export type MilageInsert = Omit<MileageUpdate, 'uuid' | 'id' | 'user_id'>;
export type ExpenseWithHistory = z.infer<typeof selectExpenseWithHistorySchema>;
export type MileageWithHistory = z.infer<typeof selectMileageWithHistorySchema>;
export const selectCategoryRulesSchema = selectCategorySchema.extend({
  rules: z.map(RuleEnum, selectRuleSchema),
});

export type CategoryWithRules = z.infer<typeof selectCategoryRulesSchema>;

export const ExpenseScheme = z
  .object({
    uuid: z.string(),
    date: z.string(),
    issuer: z.string(),
    amount: z.string(),
    //hasReceipt: z.boolean(),
    status: ExpenseStatusEnum,
  })
  .array();
export const ReportsScheme = z
  .object({
    uuid: z.string(),
    date: z.string(),
    title: z.string(),
    amount: z.string(),
    status: ReportStatusEnum,
  })
  .array();
export const ReportScheme = z.object({
  uuid: z.string(),
  date: z.string(),
  title: z.string(),
  amount: z.string(),
  status: ReportStatusEnum,
});

export const TodosSummarySchema = z.object({
  open: z.number(),
  overdue: z.number(),
  rejected: z.number(),
  approving: z.number(),
  approved: z.number(),
  unassigned_open: z.number(),
  unassigned_overdue: z.number(),
  unassigned_rejected: z.number(),
  unassigned_approving: z.number(),
  unassigned_approved: z.number(),
});
export type TodosSummary = z.infer<typeof TodosSummarySchema>;

export const ReportsSummarySchema = z.object({
  open: z.number(),
  approving: z.number(),
  approved: z.number(),
  done: z.number(),
  rejected: z.number(),
  overdue: z.number(),
});
export type ReportsSummary = z.infer<typeof ReportsSummarySchema>;

export const ReportCollectionScheme = z.object({
  id: z.number(),
  title: z.string(),
  author: z.string(),
  uuid: z.string(),
  userId: z.number(),
  description: z.string().optional(),
  comment: z.string().optional().nullable(),
  date: z.string(),
  amount: z.number(),
  currency: z.string(),
  mileages: z.array(selectMileageSchema).optional(),
  expenses: z.array(selectExpensesSchema).optional(),
  expense_count: z.number().optional(),
  mileage_count: z.number().optional(),
  status: z.enum(['open', 'rejected', 'approved', 'approving', 'overdue', 'done']),
  hasWarnings: z.boolean().optional(),
  hasRejected: z.boolean().optional(),
  hasDuplicates: z.boolean().optional(),
});

export const historyScheme = z
  .object({
    status: ExpenseStatusEnum,
    reason: z.string().optional(),
    date: z.string(),
    userId: z.number(),
  })
  .array();

export type History = z.infer<typeof historyScheme>;

export const ReportCollectionsScheme = ReportCollectionScheme.extend({
  oldestDate: z.string(),
  newestDate: z.string(),
}).array();

export type ReportCollections = z.infer<typeof ReportCollectionsScheme>;
export type ReportCollection = z.infer<typeof ReportCollectionScheme>;

export type RouteDetails = {
  origin: {
    location: google.maps.LatLng | null | undefined;
    description: string | undefined;
  };
  destination: {
    location: google.maps.LatLng | null | undefined;
    description: string | undefined;
  };
  waypoints: {
    location: google.maps.LatLng | null | undefined;
    description: string | undefined;
  }[];
};

export type ExportType = 'costcentre' | 'category' | 'client';
// export type Expense = {
//   uuid: string;
//   title?: string;
//   date: string;
//   issuer: string;
//   category?: number;
//   country?: string;
//   amount: string;
//   currency?: string;
//   vat?: number;
//   client?: number;
//   costCentre?: number;
//   hasReceipt: boolean;
//   comment?: string;
//   status: ExpenseStatus;
// };

export type Report = {
  uuid: string;
  date: string;
  title: string;
  amount: string;
  status: ReportStatus;
};

export type SwitchSelectorProps = [string, string];
export type Roles = 'user' | 'manager' | 'accountant';

export type Settlement = {
  id: number;
  uuid: string;
  title: string;
  from: string;
  to: string;
  transactions: SettlementTransaction[];
  currency: string;
  status: 'processing' | 'archived' | 'open';
};

export type SettlementTransaction = {
  id: number;
  amount: string;
  currency: string;
  made_on?: Date | null;
  description: string;
  expense_id: number | null;
  hidden: boolean;
  status: ExpenseStatus;
};

export type OrderingColumnKeys =
  | 'date'
  | 'employee'
  | 'issuer'
  | 'amount'
  | 'category'
  | 'client'
  | 'user'
  | 'status';

export type AreaChartData = { month: string; [key: string]: number | string }[];
export type AreaChartDataRaw = { month: number; [key: string]: number | string }[];

export type InviteRoles = OrgRoleName;
export const APP_KEYS = ['DMS', 'BUDGET', 'EXPENSES'] as const;
export type AppKeys = (typeof APP_KEYS)[number];
type AppIdKeys = Record<AppKeys, number>;
export const AppId: AppIdKeys = {
  DMS: 1,
  BUDGET: 2,
  EXPENSES: 3,
};

export const UserMembershipSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  name: z.string(),
  role: OrgRoleEnum,
  seat_status: z.enum(['active', 'inactive', 'pending_deactivation']),
  hide_company_setup_banner: z.boolean().optional().default(false),
});

export type UserMembership = z.infer<typeof UserMembershipSchema>;

export type MerchantMapping = {
  id: number;
  organization_id: number;
  original_merchant: string;
  display_merchant: string;
  created_at: string;
  updated_at: string;
};

export type AnalyticsExportFilters = {
  from?: string;
  to?: string;
  userIds?: string[];
  clients?: string[];
  costcentres?: string[];
  private?: boolean;
  owed?: boolean;
  agte?: number;
  alte?: number;
};
